import { ConnectionState, createLocalTracks, Room, RoomEvent, Track, VideoPresets, } from 'livekit-client';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { randomString } from 'vcs-common';
export function useLiveKitEchoTest(localVideoRef, remoteVideoRef, generateToken, onRemoteStreamReceived) {
    const roomOptions = {
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
            resolution: VideoPresets.h540.resolution,
            frameRate: 30,
        },
        publishDefaults: {
            simulcast: true,
            videoSimulcastLayers: [
                VideoPresets.h540
            ],
            dtx: true,
            stopMicTrackOnMute: false,
        },
    };
    const [roomLocal] = useState(() => new Room(roomOptions));
    const [roomRemote] = useState(() => new Room(roomOptions));
    const isConnectingRef = useRef(false);
    const isConnectedRef = useRef(false);
    const mediaStreamRef = useRef(new MediaStream());
    const connectRemoteRoom = useCallback(async (roomName, remoteIdentity) => {
        const remoteTokenRequest = {
            code: null,
            is_echo_test: true,
            room_name: roomName,
            server_type: 'livekit',
            user_identity: remoteIdentity,
        };
        const remoteTokenResult = await generateToken(remoteTokenRequest);
        if (!remoteTokenResult?.token) {
            toast.error('При подключении к серверу произошла непредвиденная ошибка');
            console.error(`Empty remote token result for room ${roomName}, user ${remoteIdentity}`);
            return;
        }
        await roomRemote.connect(`${import.meta.env.VITE_REACT_APP_LIVEKIT_URL}`, remoteTokenResult.token, {
            autoSubscribe: true,
        });
        console.debug(`Connected to remote room ${roomRemote.name}`);
        roomRemote.on(RoomEvent.Disconnected, (reason) => {
            console.debug(`Disconnected from remote room with reason ${reason}`);
            isConnectedRef.current = false;
        });
        roomRemote.on(RoomEvent.TrackSubscribed, (remoteTrack, publication, participant) => {
            console.debug(`Subscribed track from user ${participant.identity} kind ${remoteTrack.kind}, sid ${publication.track?.sid}`);
            if (participant.identity === roomLocal.localParticipant.identity && remoteVideoRef.current) {
                mediaStreamRef.current.addTrack(remoteTrack.mediaStreamTrack);
                if (remoteVideoRef.current.srcObject !== mediaStreamRef.current) {
                    remoteVideoRef.current.srcObject = mediaStreamRef.current;
                    onRemoteStreamReceived?.();
                }
            }
        });
        roomRemote.remoteParticipants?.forEach((participant, key) => {
            participant.trackPublications.forEach((publication) => {
                if (!publication.isSubscribed) {
                    publication.setSubscribed(true);
                }
            });
        });
    }, [generateToken, remoteVideoRef, roomLocal, roomRemote]);
    const connectLocalRoom = useCallback(async (roomName, localIdentity, selectedVideoDeviceId, selectedAudioDeviceId) => {
        const localTokenRequest = {
            code: null,
            is_echo_test: true,
            room_name: roomName,
            server_type: 'livekit',
            user_identity: localIdentity,
        };
        const localTokenResult = await generateToken(localTokenRequest);
        if (!localTokenResult?.token) {
            toast.error('При подключении к серверу произошла непредвиденная ошибка');
            console.error(`Empty local token result for room ${roomName}, user ${localIdentity}`);
            return;
        }
        // Do not call createLocalTracks here: handled by LiveKit internally after getUserMedia pre-init
        await roomLocal.connect(`${import.meta.env.VITE_REACT_APP_LIVEKIT_URL}`, localTokenResult.token, {
            autoSubscribe: false,
        });
        console.debug(`Connected to local room ${roomLocal.name}`);
        // Publish tracks for selected devices
        let localTracks = [];
        try {
            localTracks = await createLocalTracks({
                audio: selectedAudioDeviceId
                    ? { deviceId: { exact: selectedAudioDeviceId } }
                    : selectedAudioDeviceId !== ''
                        ? true
                        : false,
                video: selectedVideoDeviceId
                    ? { deviceId: { exact: selectedVideoDeviceId } }
                    : selectedVideoDeviceId !== ''
                        ? true
                        : false,
            });
            console.debug('Created local tracks');
        }
        catch (error) {
            console.error('Failed to initialize local tracs for devices', error);
        }
        if (localTracks) {
            for (const t of localTracks) {
                console.debug(`Publishing track ${t.kind}`);
                try {
                    const pub = await roomLocal.localParticipant.publishTrack(t);
                    console.debug(`Published track ${t.kind}, id = ${t.id}, pub.track.id = ${pub.track?.id}, sid ${pub.track?.sid}`);
                }
                catch (error) {
                    console.error(`Failed to publish track ${t.kind}`, error);
                }
            }
        }
        roomLocal.on(RoomEvent.Disconnected, (reason) => {
            console.debug(`Disconnected from local room ${reason}`);
            isConnectedRef.current = false;
            if (localTracks) {
                for (const t of localTracks) {
                    t.stop();
                }
            }
        });
    }, [generateToken, roomLocal]);
    const start = useCallback(async (selectedVideoDeviceId, selectedAudioDeviceId) => {
        if (isConnectedRef.current || isConnectingRef.current) {
            return;
        }
        isConnectingRef.current = true;
        // Safari workaround: реально открываем камеру/микрофон и показываем локально
        try {
            const constraints = {
                video: selectedVideoDeviceId ? { deviceId: { exact: selectedVideoDeviceId } } : true,
                audio: selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true,
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (localVideoRef?.current) {
                localVideoRef.current.srcObject = stream;
                await localVideoRef.current.play().catch(() => { });
            }
            console.debug('✅ Media access granted and attached to local video (Safari workaround)');
        }
        catch (e) {
            console.error('❌ Failed to init media devices', e);
            toast.error('Не удалось получить доступ к камере или микрофону');
            isConnectingRef.current = false;
            return;
        }
        console.debug('Connection to rooms');
        const opaqueId = randomString(12);
        const localIdentity = `echoTest-local-${opaqueId}-${Date.now()}`;
        const roomName = `echoTest-room-${opaqueId}-${Date.now()}`;
        const remoteIdentity = `echoTest-remote-${opaqueId}-${Date.now()}`;
        try {
            await Promise.all([
                connectLocalRoom(roomName, localIdentity, selectedVideoDeviceId, selectedAudioDeviceId),
                connectRemoteRoom(roomName, remoteIdentity),
            ]);
        }
        catch (error) {
            toast.error('При подключении к серверу произошла непредвиденная ошибка');
            console.error('Failed to connect to echo tests rooms', error);
            if (roomLocal.state === ConnectionState.Connected) {
                await roomLocal.disconnect(true);
            }
            if (roomRemote.state === ConnectionState.Connected) {
                await roomRemote.disconnect();
            }
        }
        isConnectingRef.current = false;
        isConnectedRef.current = true;
        console.debug('Connected to rooms');
    }, [connectLocalRoom, connectRemoteRoom, roomLocal, roomRemote, localVideoRef]);
    const stop = useCallback(async () => {
        if (isConnectedRef.current) {
            console.debug('Disconnecting from rooms');
            isConnectedRef.current = false;
            await Promise.all([roomLocal.disconnect(true), roomRemote.disconnect(true)]);
        }
        // Stop all tracks in local video and set srcObject to null
        if (localVideoRef?.current && localVideoRef.current.srcObject instanceof MediaStream) {
            const tracks = localVideoRef.current.srcObject.getTracks();
            tracks.forEach((track) => track.stop());
            localVideoRef.current.srcObject = null;
        }
        // Stop all tracks in remote video and set srcObject to null
        if (remoteVideoRef?.current && remoteVideoRef.current.srcObject instanceof MediaStream) {
            const tracks = remoteVideoRef.current.srcObject.getTracks();
            tracks.forEach((track) => track.stop());
            remoteVideoRef.current.srcObject = null;
        }
    }, [roomLocal, roomRemote, localVideoRef, remoteVideoRef]);
    const createDeviceTrack = useCallback(async (deviceId, kind) => {
        try {
            const newDeviceStream = await navigator.mediaDevices.getUserMedia({
                audio: kind === 'audio' ? { deviceId: deviceId } : false,
                video: kind === 'video' ? { deviceId: deviceId } : false,
            });
            const newMediaStreamTrack = newDeviceStream.getTracks()?.find((t) => t.kind === kind);
            if (!newMediaStreamTrack) {
                console.error(`Empty ${kind} track for device ${deviceId}`);
                return;
            }
            // остановка других трэков устройства
            newDeviceStream.getTracks().forEach((t) => {
                if (t !== newMediaStreamTrack) {
                    t.stop();
                }
            });
            return newMediaStreamTrack;
        }
        catch (error) {
            console.error(`Failed to create ${kind} track for device ${deviceId}`, error);
            return;
        }
    }, []);
    const switchCamera = useCallback(async (deviceId) => {
        if (!isConnectedRef.current)
            return;
        console.debug(`Switching camera to device ${deviceId}`);
        const videoTrackPub = roomLocal.localParticipant.getTrackPublication(Track.Source.Camera);
        console.debug('Stopping previous device track');
        videoTrackPub?.track?.mediaStreamTrack?.stop();
        console.debug(`Restarting video track for device id ${deviceId}`);
        const newMediaStreamTrack = await createDeviceTrack(deviceId, 'video');
        if (!newMediaStreamTrack) {
            toast.error('Не удалось подключить камеру');
            return;
        }
        console.debug(`Replacing with new video track ${newMediaStreamTrack.id} enabled ${newMediaStreamTrack.enabled}, readyState = ${newMediaStreamTrack.readyState}`);
        await videoTrackPub?.track?.replaceTrack(newMediaStreamTrack, {
            userProvidedTrack: false,
        });
        // Update localVideoRef with the new track
        if (localVideoRef?.current && newMediaStreamTrack) {
            // Store old stream before replacing
            const oldStream = localVideoRef.current?.srcObject;
            // Safari workaround: assign newDeviceStream if available, else create a new MediaStream
            let isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            if (isSafari) {
                // For Safari, getUserMedia returns a stream with a single video track
                const newDeviceStream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: deviceId },
                    audio: false,
                });
                localVideoRef.current.srcObject = newDeviceStream;
            }
            else {
                localVideoRef.current.srcObject = new MediaStream([newMediaStreamTrack]);
            }
            await localVideoRef.current.play().catch(() => { });
            // After playing, stop all tracks in the old stream
            if (oldStream) {
                oldStream.getTracks().forEach((track) => track.stop());
            }
        }
        return newMediaStreamTrack;
    }, [createDeviceTrack, roomLocal, localVideoRef]);
    const switchMicrophone = useCallback(async (deviceId) => {
        console.debug(`Selecting microphone ${deviceId}`);
        if (!isConnectedRef.current)
            return;
        console.debug(`Switching microphone to device ${deviceId}`);
        const audioTrackPub = roomLocal.localParticipant.getTrackPublication(Track.Source.Microphone);
        console.debug(`Restarting audio track for device id ${deviceId}`);
        const audioTrack = audioTrackPub?.track;
        await audioTrack?.restartTrack({ deviceId: deviceId });
        return audioTrack?.mediaStreamTrack;
    }, [roomLocal]);
    return {
        initialized: !!roomLocal && !!roomRemote,
        start: start,
        stop: stop,
        changeAudioDevice: switchMicrophone,
        changeVideoDevice: switchCamera,
    };
}
